import {
    IAccountShippingPreferences,
    IShippingPreference,
} from '../../models/shipping-preferences';
import { useDispatch } from 'react-redux';
import React, { useEffect } from 'react';
import { beginApiCall, apiCallError } from '../api/api-status-action';
import {
    getDefaultShippingPreferences,
    saveShippingPreferences,
} from '../../api/shipping-preferences-api';
import {
    loadShippingPreferencesSuccess,
    saveShippingPreferencesSuccess,
} from './shipping-preferences-actions';
import { getErrorMessage } from '../../utils/error-handling';

const LOAD_SHIPPING_PREFERENCES = 'LOAD_SHIPPING_PREFERENCES';
const SAVE_SHIPPING_PREFERENCES = 'SAVE_SHIPPING_PREFERENCES';

export const useShippingPreferences = (
    billTo: string,
    shipTo: string,
    reset = false
): [
    IAccountShippingPreferences,
    () => void,
    string, // error message,
    (prefs: IShippingPreference) => Promise<boolean>,
    boolean // loading
] => {
    const [isLoading, setIsLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [shippingPreferences, setShippingPreferences] =
        React.useState<IAccountShippingPreferences | null>(null);

    const dispatch = useDispatch();

    const fetchData = async () => {
        try {
            dispatch(beginApiCall(LOAD_SHIPPING_PREFERENCES));
            const response = await getDefaultShippingPreferences(
                billTo,
                shipTo
            );

            if (response.ok) {
                setShippingPreferences({ ...response.data! });
            }
            dispatch(loadShippingPreferencesSuccess([]));
        } catch (error) {
            dispatch(apiCallError(error, LOAD_SHIPPING_PREFERENCES));
            setError(getErrorMessage(error));
        }

        setIsLoading(false);
    };

    const saveData = async (
        shippingPreference: IShippingPreference
    ): Promise<boolean> => {
        try {
            dispatch(beginApiCall(SAVE_SHIPPING_PREFERENCES));
            const response = await saveShippingPreferences(shippingPreference);
            dispatch(saveShippingPreferencesSuccess());

            return response.ok!;
        } catch (error) {
            dispatch(apiCallError(error, SAVE_SHIPPING_PREFERENCES));
            setError(getErrorMessage(error));

            return false;
        }
    };

    const reload = () => {
        if (billTo && shipTo) {
            fetchData();
            console.log(`reload: ${billTo}/${shipTo}`);
        }
    };

    useEffect(() => {
        if (billTo && shipTo) {
            setIsLoading(true);
            fetchData();
        }
    }, [billTo, shipTo]);

    useEffect(() => {
        if (reset && shippingPreferences) {
            setShippingPreferences(null);
        }
    }, [reset]);

    return [shippingPreferences!, reload, error, saveData, isLoading];
};
